import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Settings2, X, Store } from "lucide-react";
import { toast } from "sonner";

const errMsg = (e) => formatApiError(e.response?.data?.detail);
const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtD = (d) => (d ? d.split("-").reverse().join("/") : "");
const MODES = {
  off: ["Oculto", "bg-slate-100 text-slate-600"],
  all: ["Todos os alunos", "bg-green-50 text-green-700"],
  selected: ["Só quem eu escolher", "bg-sky-50 text-sky-700"],
};

/** Loja na área do aluno: o admin decide quais cursos aparecem para compra e para quem (campanhas). */
export default function ShopAdmin() {
  const [rows, setRows] = useState(null);
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/admin/shop").then((r) => setRows(r.data)).catch((e) => { toast.error(errMsg(e)); setRows([]); }), []);
  useEffect(() => {
    load();
    api.get("/groups").then((r) => setGroups(r.data)).catch(() => {});
    api.get("/students").then((r) => setStudents(r.data)).catch(() => {});
  }, [load]);

  const byId = useMemo(() => Object.fromEntries(students.map((s) => [s.id, s])), [students]);
  const matches = useMemo(() => {
    if (!dlg || search.trim().length < 2) return [];
    const q = search.trim().toLowerCase();
    return students.filter((s) => !dlg.student_ids.includes(s.id) && ((s.name || "").toLowerCase().includes(q) || (s.email || "").toLowerCase().includes(q))).slice(0, 8);
  }, [dlg, search, students]);

  const open = (r) => { setSearch(""); setDlg({ ...r, student_ids: [...r.student_ids], group_ids: [...r.group_ids] }); };
  const set = (k, v) => setDlg((p) => ({ ...p, [k]: v }));
  const toggleGroup = (id) => setDlg((p) => ({ ...p, group_ids: p.group_ids.includes(id) ? p.group_ids.filter((x) => x !== id) : [...p.group_ids, id] }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/${dlg.kind === "combo" ? "combos" : "courses"}/${dlg.id}/shop`, { mode: dlg.mode, student_ids: dlg.student_ids, group_ids: dlg.group_ids, from_date: dlg.from_date, until_date: dlg.until_date });
      toast.success("Loja atualizada."); setDlg(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (rows === null) return <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const audience = (r) => r.mode === "selected"
    ? [r.group_ids.length && `${r.group_ids.length} turma(s)`, r.student_ids.length && `${r.student_ids.length} aluno(s)`].filter(Boolean).join(" + ")
    : r.mode === "all" ? "Todos os alunos" : "—";

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><Store className="h-7 w-7 text-[#1E9E2A]" /> Loja e campanhas</h1>
        <p className="text-slate-500 mt-1 max-w-3xl">
          Controle quais cursos aparecem para <strong>comprar</strong> na área do aluno. Por padrão nenhum aparece. Libere para todos, ou só para as turmas e alunas de uma campanha, com data de início e fim.
          Só aparecem cursos publicados e com preço maior que zero, e o aluno nunca vê um curso em que já está matriculado.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto iep-scroll">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-50/60">
            <th className="px-4 py-3">Curso</th><th className="px-3 py-3">Preço</th><th className="px-3 py-3">Na loja</th><th className="px-3 py-3 hidden md:table-cell">Quem vê</th><th className="px-3 py-3 hidden md:table-cell">Período</th><th className="px-4 py-3 text-right">Ações</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => {
              const [label, cls] = MODES[r.mode] || MODES.off;
              const semPreco = !(r.price > 0);
              return (
                <tr key={r.id} data-testid={`shop-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{r.kind === "combo" && <span className="mr-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 align-middle">COMBO</span>}{r.title}{!r.published && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">rascunho</span>}</td>
                  <td className="px-3 py-3 text-slate-600">{semPreco ? <span className="text-xs text-slate-400">sem preço</span> : (r.promo_price > 0 ? <><span className="text-xs text-slate-400 line-through mr-1.5">{brl(r.price)}</span><span className="font-semibold text-[#1E9E2A]">{brl(r.promo_price)}</span></> : brl(r.price))}</td>
                  <td className="px-3 py-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`}>{label}</span></td>
                  <td className="px-3 py-3 hidden md:table-cell text-slate-600">{audience(r)}</td>
                  <td className="px-3 py-3 hidden md:table-cell text-slate-600 whitespace-nowrap">{r.from_date || r.until_date ? `${fmtD(r.from_date) || "…"} a ${fmtD(r.until_date) || "…"}` : "sem período"}</td>
                  <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => open(r)} data-testid={`shop-config-${r.id}`}><Settings2 className="h-3.5 w-3.5 mr-1" /> Configurar</Button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Loja: {dlg?.title}</DialogTitle>
            <DialogDescription>Defina quem vê este curso para comprar na área do aluno.</DialogDescription>
          </DialogHeader>
          {dlg && (
            <div className="space-y-4 py-1">
              {!(dlg.price > 0) && <p className="text-xs rounded-md bg-amber-50 text-amber-800 px-3 py-2">Este curso está sem preço, então não aparece na loja mesmo liberado. Defina o preço em Cursos → editar curso.</p>}
              <div className="space-y-1.5">
                <Label>Quem pode ver na loja</Label>
                <select value={dlg.mode} onChange={(e) => set("mode", e.target.value)} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="shop-mode">
                  <option value="off">Oculto (ninguém vê)</option>
                  <option value="all">Todos os alunos</option>
                  <option value="selected">Só as turmas e alunas que eu escolher (campanha)</option>
                </select>
              </div>
              {dlg.mode === "selected" && (
                <>
                  <div className="space-y-1.5">
                    <Label>Turmas</Label>
                    <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-40 overflow-y-auto">
                      {groups.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">Nenhuma turma cadastrada.</p>}
                      {groups.map((g) => (
                        <label key={g.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                          <input type="checkbox" checked={dlg.group_ids.includes(g.id)} onChange={() => toggleGroup(g.id)} />
                          <span className="flex-1">{g.name}</span><span className="text-xs text-slate-400">{(g.student_ids || []).length} alunos</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Alunos(as) específicos</Label>
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Digite o nome ou e-mail para adicionar…" data-testid="shop-student-search" />
                    {matches.length > 0 && (
                      <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-44 overflow-y-auto">
                        {matches.map((s) => (
                          <button key={s.id} type="button" onClick={() => { set("student_ids", [...dlg.student_ids, s.id]); setSearch(""); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">
                            <div className="font-medium text-slate-800">{s.name}</div><div className="text-xs text-slate-400">{s.email}</div>
                          </button>
                        ))}
                      </div>
                    )}
                    {dlg.student_ids.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {dlg.student_ids.map((id) => (
                          <span key={id} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 rounded-full pl-2.5 pr-1 py-1">
                            {byId[id]?.name || "Aluno(a)"}
                            <button type="button" onClick={() => set("student_ids", dlg.student_ids.filter((x) => x !== id))} className="p-0.5 hover:text-red-600"><X className="h-3 w-3" /></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
              {dlg.mode !== "off" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Campanha começa (opcional)</Label><Input type="date" value={dlg.from_date} onChange={(e) => set("from_date", e.target.value)} /></div>
                  <div className="space-y-1.5"><Label>Campanha termina (opcional)</Label><Input type="date" value={dlg.until_date} onChange={(e) => set("until_date", e.target.value)} /></div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="text-white" style={{ background: "#1E9E2A" }} data-testid="shop-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
